import React, { PropsWithChildren, ReactNode, useEffect } from "react";

import {
  Button,
  Dialog,
  LinearProgress,
  makeStyles,
  Typography
} from "@material-ui/core";

import { MdCheckCircle, MdKeyboardArrowRight } from "react-icons/md";

import HStack from "modules/core/components/HStack";
import StepsIndicator from "./StepsIndicator";

import { useBoolean } from "modules/core/hooks/useBoolean";

import sideBG from "modules/core/assets/img/onboarding/side-bg.png";

interface IBaseOnboardingModalProps {
  stepsCount: number;
  currentStep: number;
  title: string;
  sideContent: ReactNode;
  status?: string;
  submitLabel: string;
  isSubmitting?: boolean;
  goNext: () => void;
  onSubmit: () => void;
}

const useStyles = makeStyles(theme => ({
  row: {
    width: "100%",
    height: 500
  },

  main: {
    flexGrow: 1,
    flexShrink: 1,
    padding: "64px 64px 20px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  },

  side: {
    backgroundImage: `url(${sideBG})`,
    backgroundColor: "#120115",
    backgroundSize: "contain",
    backgroundRepeat: "repeat",
    width: 350,
    height: "100%",
    flexShrink: 0,
    flexGrow: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  loadingBar: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    borderRadius: "4px 4px 0 0",
    overflow: "hidden"
  },

  statusMsg: {
    marginTop: 4,
    color: theme.palette.grey[500],
    height: 16
  },

  successIcon: {
    color: theme.palette.success.main,
    lineHeight: 0
  }
}));

const BaseOnboardingModal = (
  props: PropsWithChildren<IBaseOnboardingModalProps>
) => {
  const {
    stepsCount,
    currentStep,
    title,
    status,
    children,
    sideContent,
    submitLabel,
    isSubmitting,
    goNext,
    onSubmit
  } = props;

  const [isLoadingBarVisible, { on: showLoadingBar, off: hideLoadingBar }] =
    useBoolean();

  const isLastStep = currentStep + 1 === stepsCount;

  useEffect(() => {
    // start loading for 3 seconds when currentStep changes
    if (!isLastStep) {
      showLoadingBar();

      const timeout = setTimeout(hideLoadingBar, 3000);

      return () => clearTimeout(timeout);
    }
  }, [hideLoadingBar, showLoadingBar, currentStep, isLastStep]);

  const classes = useStyles();

  return (
    <Dialog open maxWidth="md" fullWidth scroll="body">
      {(isLoadingBarVisible || isSubmitting) && (
        <div className={classes.loadingBar}>
          <LinearProgress />
        </div>
      )}

      <HStack className={classes.row}>
        <div className={classes.main}>
          <div>
            <div style={{ marginBottom: 15 }}>
              {isLastStep ? (
                <HStack gap={7}>
                  <span className={classes.successIcon}>
                    <MdCheckCircle size={32} />
                  </span>

                  <Typography variant="subtitle1" color="textSecondary">
                    Setup Completed!
                  </Typography>
                </HStack>
              ) : (
                <StepsIndicator
                  stepsCount={stepsCount}
                  currentStep={currentStep}
                />
              )}
            </div>

            <Typography
              variant="h5"
              style={{ marginBottom: 25, fontWeight: 700 }}
            >
              {title}
            </Typography>

            {children}
          </div>

          <HStack style={{ justifyContent: "flex-end" }}>
            <div style={{ textAlign: "right" }}>
              <Button
                variant="contained"
                color="primary"
                endIcon={<MdKeyboardArrowRight />}
                onClick={isLastStep ? onSubmit : goNext}
                disabled={isLoadingBarVisible || isSubmitting}
              >
                {isLastStep ? submitLabel : "Next Step"}
              </Button>

              <Typography
                variant="caption"
                color="textSecondary"
                component="p"
                className={classes.statusMsg}
              >
                {isLoadingBarVisible ? status : ""}
              </Typography>
            </div>
          </HStack>
        </div>

        <div className={classes.side}>{sideContent}</div>
      </HStack>
    </Dialog>
  );
};

export default React.memo(BaseOnboardingModal);
